import { Hammer, Check } from 'lucide-react';
import microsoftLogo from '@assets/logos/microsoft.svg';
import paytmLogo from '@assets/logos/paytm.svg';
import makemytripLogo from '@assets/logos/makemytrip.svg';
import flipkartLogo from '@assets/logos/flipkart.svg';
import expediaLogo from '@assets/logos/expedia.svg';
import truecallerLogo from '@assets/logos/truecaller.svg';
import hitachiLogo from '@assets/logos/hitachi.svg';
import uberLogo from '@assets/logos/uber.svg';
import tcsLogo from '@assets/logos/tcs.svg';
import oracleLogo from '@assets/logos/oracle.svg';
import accentureLogo from '@assets/logos/accenture.svg';
import ibmLogo from '@assets/logos/ibm.svg';
import epamLogo from '@assets/logos/epam.svg';
import type { FlowIntent } from './types';
import { AgentBuildWindow } from './AgentBuildWindow';
import { LogoMarquee } from './LogoMarquee';
import { PrimaryCta, studioCtaStyles } from './studioCtas';
import { DERISK_POINTS } from './startFreeContent';

interface StudioHeroProps {
  onFlow: (intent: FlowIntent) => void;
}

const PROOF_LOGOS = [
  { src: microsoftLogo, alt: 'Microsoft', height: 19 },
  { src: uberLogo, alt: 'Uber', height: 18 },
  { src: paytmLogo, alt: 'Paytm', height: 19 },
  { src: expediaLogo, alt: 'Expedia', height: 17 },
  { src: makemytripLogo, alt: 'MakeMyTrip', height: 19 },
  { src: truecallerLogo, alt: 'Truecaller', height: 16 },
  { src: flipkartLogo, alt: 'Flipkart', height: 24 },
  { src: hitachiLogo, alt: 'Hitachi Digital Services', height: 18 },
  { src: tcsLogo, alt: 'Tata Consultancy Services', height: 19 },
  { src: oracleLogo, alt: 'Oracle', height: 26 },
  { src: accentureLogo, alt: 'Accenture', height: 26 },
  { src: ibmLogo, alt: 'IBM', height: 26 },
  { src: epamLogo, alt: 'EPAM', height: 26 },
];

export function StudioHero({ onFlow }: StudioHeroProps) {
  return (
    <section
      className="studio-hero-section"
      style={{
        background: 'var(--navy-deep)',
        padding: '60px 24px 150px',
        position: 'relative',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: '-18%',
          right: '-9%',
          width: '620px',
          height: '620px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(20,184,166,.14), transparent 60%)',
          pointerEvents: 'none',
        }}
      />
      <div
        className="studio-hero-grid"
        style={{
          width: '100%',
          maxWidth: '1120px',
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: '1.05fr .95fr',
          gap: '56px',
          alignItems: 'center',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <div className="studio-hero-copy" style={{ minWidth: 0 }}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '6px 14px',
              borderRadius: '999px',
              background: 'rgba(20,184,166,.12)',
              border: '1px solid rgba(45,212,191,.3)',
              color: 'var(--primary-light)',
              fontSize: '13px',
              fontWeight: 600,
            }}
          >
            <Hammer size={14} />
            Build · don&apos;t just learn
          </span>
          <h1
            style={{
              color: '#fff',
              fontSize: 'clamp(34px,4.4vw,50px)',
              fontWeight: 800,
              lineHeight: 1.08,
              letterSpacing: '-.025em',
              margin: '18px 0 18px',
            }}
          >
            Build Production-Ready AI Agents with{' '}
            <span style={{ color: 'var(--primary-light)' }}>OpenAI &amp; Claude</span>
          </h1>
          <p
            style={{
              color: 'rgba(248,250,252,.72)',
              fontSize: '18px',
              lineHeight: 1.6,
              maxWidth: '520px',
            }}
          >
            Use <strong style={{ color: 'rgba(248,250,252,.92)', fontWeight: 600 }}>AI Fluens Studio</strong> to go
            from first LLM call to a working agent. Nine guided modules — every one ends by shipping a real
            capability to <em>your</em> agent.
          </p>
          <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', marginTop: '28px' }}>
            <PrimaryCta onClick={() => onFlow('agent')} data-testid="hero-build-agent-button">
              Start Building AI Agents
            </PrimaryCta>
          </div>

          {/* De-risk row — surfaces $2 credits / no card / BYOK in the first viewport */}
          <ul
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '10px',
              listStyle: 'none',
              margin: '16px 0 0',
              padding: 0,
            }}
          >
            {DERISK_POINTS.map((point) => (
              <li
                key={point}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 12px',
                  borderRadius: '999px',
                  border: '1px solid rgba(45,212,191,.3)',
                  background: 'rgba(20,184,166,.08)',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: 'rgba(248,250,252,.85)',
                }}
              >
                <Check size={13} color="var(--primary-light)" aria-hidden="true" />
                {point}
              </li>
            ))}
          </ul>

          {/* Proof row */}
          <div style={{ marginTop: '36px' }}>
            <span
              style={{
                fontSize: '11.5px',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '.08em',
                color: 'rgba(248,250,252,.45)',
              }}
            >
              Trusted by engineers at
            </span>
            <LogoMarquee logos={PROOF_LOGOS} priority />
          </div>
        </div>

        <div className="studio-hero-visual" style={{ minWidth: 0, width: '100%' }}>
          <AgentBuildWindow />
        </div>
      </div>

      <style>{`
        ${studioCtaStyles}
        .studio-hero-section { overflow-x: clip; }
        @media (max-width: 767px) {
          .studio-hero-section { padding: 40px 16px 120px !important; }
          .studio-hero-grid {
            grid-template-columns: 1fr !important;
            gap: 32px !important;
          }
          .studio-hero-copy p { font-size: 16px !important; }
        }
        @media (min-width: 768px) and (max-width: 980px) {
          .studio-hero-grid { grid-template-columns: 1fr !important; gap: 40px !important; }
        }
      `}</style>
    </section>
  );
}
